import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import type { TabsProps } from "@mui/material/Tabs";
import Tabs from "@mui/material/Tabs";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AlertSettingTab } from "@/pages/billing/v2/tabs/AlertSettingTab";
import { FeaturePricingTab } from "@/pages/billing/v2/tabs/FeaturePricingTab";
import { OverviewTab } from "@/pages/billing/v2/tabs/OverviewTab";
import { TransactionHistoryTab } from "@/pages/billing/v2/tabs/TransactionHistoryTab";
import { UsageDetailsTab } from "@/pages/billing/v2/tabs/UsageDetailsTab";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

const routeApi = getRouteApi("/billing");

// --- Tab Definition ---

const BILLING_TABS = [
  "overview",
  "feature-pricing",
  "topup-history",
  "transaction-history",
  "alert-setting",
] as const;

// --- TabPanel ---

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `billing-tab-${index}`,
    "aria-controls": `billing-tabpanel-${index}`,
  };
}

// --- Main Page ---

export const BillingV2Page: React.FC = function BillingV2Page() {
  const { t } = useTranslation();
  const { tab } = routeApi.useSearch();
  const navigate = useNavigate();

  const tabValue = useMemo(
    () => BILLING_TABS.indexOf(tab ?? "overview"),
    [tab],
  );

  const handleChange = useHandler<TabsProps["onChange"]>(
    (_, newValue: number) => {
      navigate({
        to: "/billing",
        search: {
          tab: BILLING_TABS[newValue],
        },
      });
    },
  );

  return (
    <PageLayout>
      <PageTitle>{t("billing.usage.title", "Billing & usage")}</PageTitle>
      <Tabs value={tabValue} onChange={handleChange}>
        <Tab label={t("billing.tabs.overview")} {...a11yProps(0)} />
        <Tab label={t("billing.tabs.featurePricing")} {...a11yProps(1)} />
        <Tab label={t("billing.tabs.topUpHistory")} {...a11yProps(2)} />
        <Tab label={t("billing.tabs.transactionHistory")} {...a11yProps(3)} />
        <Tab label={t("billing.tabs.alertSetting")} {...a11yProps(4)} />
      </Tabs>
      <TabPanel value={tabValue} index={0}>
        <OverviewTab />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <FeaturePricingTab />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <UsageDetailsTab />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <TransactionHistoryTab />
      </TabPanel>
      <TabPanel value={tabValue} index={4}>
        <AlertSettingTab />
      </TabPanel>
    </PageLayout>
  );
};
